// Loads the capture AudioWorklet and wraps its node (C-7).
//
// The module is the built asset `pcm-capture.worklet.js` beside app.js. If a
// shell refuses the URL (for example an old WebKit on capacitor://), the same
// asset is fetched and loaded from a Blob URL; the CSP already allows `blob:`
// in script-src. No processor source is ever built from strings at runtime.
import { PCM_CAPTURE_PROCESSOR, PCM_CAPTURE_WORKLET_ASSET } from '../contracts';
import type { FramedPcm } from './framer';
import type { CaptureWorkletMessage } from './pcm-capture.worklet';

/** How long `flush()` waits for the worklet's answer before giving up. */
export const FLUSH_TIMEOUT_MS = 250;

export type CaptureNode = {
  readonly node: AudioWorkletNode;
  /** Ends the capture: emits the tail frame, then resolves with the final counts. */
  flush(): Promise<{ frames: number; samples: number } | null>;
  disconnect(): void;
};

const loadedContexts = new WeakSet<BaseAudioContext>();

export function captureWorkletUrl(): string {
  return new URL(`./${PCM_CAPTURE_WORKLET_ASSET}`, import.meta.url).href;
}

export async function loadCaptureWorklet(
  context: BaseAudioContext,
  url = captureWorkletUrl(),
  fetchImpl: typeof fetch = (...args) => globalThis.fetch(...args),
): Promise<void> {
  if (loadedContexts.has(context)) return;
  try {
    await context.audioWorklet.addModule(url);
  } catch (error) {
    const response = await fetchImpl(url).catch(() => null);
    if (!response?.ok) throw error;
    const blobUrl = URL.createObjectURL(
      new Blob([await response.text()], { type: 'text/javascript' }),
    );
    try {
      await context.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }
  loadedContexts.add(context);
}

type NodeFactory = (context: BaseAudioContext, emitActivity?: boolean) => AudioWorkletNode;

const defaultNode: NodeFactory = (context, emitActivity = false) =>
  new AudioWorkletNode(context, PCM_CAPTURE_PROCESSOR, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    channelCount: 1,
    channelCountMode: 'explicit',
    channelInterpretation: 'speakers',
    processorOptions: { emitActivity },
  });

export function createCaptureNode(
  context: BaseAudioContext,
  onFrame: (frame: FramedPcm) => void,
  createNode: NodeFactory = defaultNode,
  onActivity?: (level: number, samples?: Float32Array, sampleOffset?: number) => void,
): CaptureNode {
  const node = createNode(context, Boolean(onActivity));
  let flushId = 0;
  const pending = new Map<number, (value: { frames: number; samples: number } | null) => void>();
  node.port.onmessage = (event: MessageEvent<CaptureWorkletMessage>) => {
    const message = event.data;
    if (message?.type === 'frame') {
      const { type: _type, ...frame } = message;
      onFrame(frame);
    } else if (message?.type === 'activity') {
      onActivity?.(message.level, message.samples, message.sampleOffset);
    } else if (message?.type === 'flushed') {
      pending.get(message.id)?.({ frames: message.frames, samples: message.samples });
    }
  };
  return {
    node,
    flush: () =>
      new Promise((resolve) => {
        const id = (flushId += 1);
        const finish = (value: { frames: number; samples: number } | null) => {
          clearTimeout(timer);
          pending.delete(id);
          resolve(value);
        };
        const timer = setTimeout(() => finish(null), FLUSH_TIMEOUT_MS);
        pending.set(id, finish);
        node.port.postMessage({ type: 'flush', id });
      }),
    disconnect: () => {
      node.port.onmessage = null;
      node.disconnect();
    },
  };
}
